import { ArrowRightOutlined } from '@ant-design/icons';
import {
  EnumRichiestaDTOStato,
  getGetFrontRichiesteIdQueryKey,
  usePostFrontRichiesteIdAzioniInvia,
} from '@framework/api-client';
import { getNetworkErrorMessage } from '@framework/auth';
import { useTranslation } from '@framework/i18n';
import { generatePath, useHistory } from '@framework/ui';
import { Button, ButtonProps, Tooltip, message } from 'antd';
import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { queryClient } from '../../../../core/network/queryClient';

const FullHeightButton = styled(Button)`
  &&& {
    height: 100%;
  }
`;

export interface ISubmissionSendButtonProps extends ButtonProps {
  submissionId: string | null;
  text: string;
  isCompleted: boolean;
  hasPendingPayments: boolean;
  cannotSendAsDelegate: boolean;
  tooltip?: string;
}

export function SubmissionSendButton(props: ISubmissionSendButtonProps) {
  const {
    submissionId,
    text,
    isCompleted,
    hasPendingPayments,
    cannotSendAsDelegate,
    disabled,
    tooltip,
    ...otherProps
  } = props;
  const { t } = useTranslation();

  const send = usePostFrontRichiesteIdAzioniInvia();
  const history = useHistory();

  const tooltipText = useMemo(() => {
    if (tooltip) return tooltip;
    if (!isCompleted) {
      return t(
        'form.form-not-completed-tooltip',
        'Compilazione incompleta',
      );
    }
    if (hasPendingPayments) {
      return t('form.form-not-payed-tooltip', 'Pagamenti mancanti');
    }
    if (cannotSendAsDelegate) {
      return t(
        'form.form-cannot-send-as-delegate-tooltip',
        "Non puoi inviare questa richiesta in quanto la delega non permette l'invio",
      );
    }
    return undefined;
  }, [t, isCompleted, hasPendingPayments, cannotSendAsDelegate]);

  const handleSend = useCallback(async () => {
    if (!submissionId) return;

    try {
      const sentSubmission = await send.mutateAsync({ id: submissionId });

      message.success(
        t(
          'form.invio-effettuato-con-successo',
          'Invio effettuato con successo',
        ),
      );
      // Aggiorniamo la cache con i dati della richiesta inviata. In questo
      // modo siamo sicuri che venga visualizzata la richiesta nello stato
      // corretto.
      queryClient.setQueryData(
        getGetFrontRichiesteIdQueryKey(submissionId),
        () => sentSubmission,
      );

      history.push(
        sentSubmission?.stato == EnumRichiestaDTOStato.IN_INVIO
          ? generatePath('/richieste/:id/risultato', { id: submissionId })
          : generatePath('/richieste/:id/inviata', { id: submissionId }),
      );
    } catch (e) {
      console.error(`[SubmissionSendButton] Error while sending submission [${submissionId}]`, {error: e}); // prettier-ignore
      message.error(getNetworkErrorMessage(e, t("form.errore-durante-invio", "Si è verificato un errore durante l'invio"))); // prettier-ignore
    }
  }, [history, send, submissionId]);

  const buttonDisabled =
    !submissionId ||
    !isCompleted ||
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    disabled ||
    hasPendingPayments ||
    cannotSendAsDelegate;

  return (
    <Tooltip title={tooltipText}>
      <FullHeightButton
        {...otherProps}
        type="primary"
        size="large"
        loading={send.isLoading}
        disabled={buttonDisabled}
        onClick={() => {
          handleSend();
        }}
      >
        {text} <ArrowRightOutlined aria-hidden />
      </FullHeightButton>
    </Tooltip>
  );
}
